// ABOUTME: Staff add a member to a client company: name and email, no password, and no email is sent.
// ABOUTME: A taken email says who has it (candidate, staff, client member, deleted account, or a former member of another company).
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Button from '$components/Buttons/Button';
import BasicInput from '$components/Inputs/BaseInput';
import FormFooter from '$components/FormFooter/FormFooter';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import { emailRegex } from '$constants/regex';
import { SIMERA_COMPANY_ROUTE } from '$constants/routes';
import { useAddCompanyMemberMutation } from '$store/modules/companies';
import type { AddCompanyMemberError } from '$store/modules/companies';
import { SaveState } from '$types/ForrmType';

interface FormData {
  FirstName: string;
  LastName: string;
  Email: string;
}

const trim = (value: string) => value.trim();

const AddMember = ({ companyId }: { companyId: number }) => {
  const [t, i18next] = useTranslation('global');
  const [isOpen, setIsOpen] = useState(false);
  const [emailTaken, setEmailTaken] = useState<AddCompanyMemberError>();
  const [addMember, { isLoading }] = useAddCompanyMemberMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: { FirstName: '', LastName: '', Email: '' },
  });

  const firstName = register('FirstName', {
    required: `${t('Employers.FirstNameError')}`,
    setValueAs: trim,
  });
  const lastName = register('LastName', {
    required: `${t('Employers.LastNameError')}`,
    setValueAs: trim,
  });
  const email = register('Email', {
    required: `${t('register.emailInputValid')}`,
    pattern: {
      value: emailRegex,
      message: `${t('register.invalidEmailInput')}`,
    },
    setValueAs: trim,
  });

  const closeModal = () => {
    setIsOpen(false);
    setEmailTaken(undefined);
    reset();
  };

  const onSubmit = async (data: FormData) => {
    setEmailTaken(undefined);
    const response = await addMember({
      companyId,
      ...data,
      lang: i18next.language as 'en' | 'es',
    });
    if (!('error' in response)) {
      closeModal();
      return;
    }
    const error = response.error as AddCompanyMemberError | undefined;
    if (error?.statusCode === 409) setEmailTaken(error);
  };

  // A user can hold several roles; each one is a different next step for ops.
  const takenMessages = () => {
    const user = emailTaken?.User;
    if (!user) return [];
    const messages = [
      user.Deleted && t('CompaniesTable.EmailTakenDeleted'),
      user.IsCandidate && t('CompaniesTable.EmailTakenCandidate'),
      user.IsStaff && t('CompaniesTable.EmailTakenStaff'),
      user.IsEmployer && t('CompaniesTable.EmailTakenMember'),
    ].filter((message): message is string => !!message);
    const { message } = emailTaken;
    const fallback = Array.isArray(message) ? message.join('. ') : message;
    return messages.length ? messages : [fallback];
  };

  return (
    <>
      <Button
        text={t('CompaniesTable.AddMember')}
        showText
        bgColor="#079e88"
        textStyles={{ color: 'white' }}
        styling="px-4"
        size="auto"
        onClick={() => setIsOpen(true)}
      />
      <ModalInTailwind isOpen={isOpen} closeModal={closeModal}>
        <form
          className="bg-white rounded p-8 flex flex-col w-full max-w-lg"
          onSubmit={handleSubmit(onSubmit)}
        >
          <h2 className="font-bold text-xl mb-4">
            {t('CompaniesTable.AddMember')}
          </h2>
          <BasicInput
            type="text"
            label={t('register.nameInput')}
            isRequired
            name={firstName.name}
            refProp={firstName.ref}
            onChange={firstName.onChange}
            onBlur={firstName.onBlur}
            errorText={errors.FirstName?.message ?? ''}
            dataTestId="member-first-name"
          />
          <BasicInput
            type="text"
            label={t('register.lastNameInput')}
            isRequired
            name={lastName.name}
            refProp={lastName.ref}
            onChange={lastName.onChange}
            onBlur={lastName.onBlur}
            errorText={errors.LastName?.message ?? ''}
            dataTestId="member-last-name"
          />
          <BasicInput
            type="text"
            label={t('register.emailInput')}
            isRequired
            name={email.name}
            refProp={email.ref}
            onChange={email.onChange}
            onBlur={email.onBlur}
            errorText={errors.Email?.message ?? ''}
            dataTestId="member-email"
          />
          <p className="mt-2 text-sm">{t('CompaniesTable.MemberSignIn')}</p>
          {emailTaken?.FormerCompany ? (
            <p className="mt-2 text-red-600">
              {t('CompaniesTable.EmailFormerMember')}{' '}
              <Link
                className="underline"
                to={SIMERA_COMPANY_ROUTE(emailTaken.FormerCompany.Id)}
              >
                {emailTaken.FormerCompany.Name}
              </Link>
              . {t('CompaniesTable.EmailFormerMemberMove')}
            </p>
          ) : (
            takenMessages().map((message) => (
              <p key={message} className="mt-2 text-red-600">
                {message}
              </p>
            ))
          )}
          <div className="mt-4">
            <FormFooter
              translate={t}
              saveState={isLoading ? SaveState.PENDING : SaveState.IDLE}
              submit
              onClickCancel={closeModal}
              disabled={isLoading}
            />
          </div>
        </form>
      </ModalInTailwind>
    </>
  );
};

export default AddMember;
